import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Icon,
  HStack
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { QueryAdminUsersApi } from '../../../../apis/QueryMaintenance.api';
import {
  chooseDataStateType,
  MaintenanceModalDispLabels
} from '../../../molecules/MaintenanceModalDispLabels';
import { MaintenanceFooter } from '../MaintenanceFooter';
import {
  ArrowUpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@chakra-ui/icons';
import {
  AdminUsersSearchCondition,
  RoleEnum,
  TableColumnEnum
} from '../../../../pages/AdminUsersMaintenance';
import { MaintenanceCompareEnum } from '../../../constants/MaintenanceConstants';

export interface AdminUsersType {
  id?: number;
  user_no?: number;
  name?: string;
  kana?: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AdminUsersStateType {
  type: 'adminUsers';
  adminUsers: AdminUsersType;
}

interface props {
  AdminUsersDatas: AdminUsersType[] | undefined;
  setMaintenanceDatas: React.Dispatch<
    React.SetStateAction<AdminUsersType[] | undefined>
  >;
  setChooseData: (data: chooseDataStateType) => void;
  onOpenBrowse: () => void;
  onOpenEdit: () => void;
  onToggle: () => void;
  Query: AdminUsersSearchCondition;
  setQuery: React.Dispatch<React.SetStateAction<AdminUsersSearchCondition>>;
  createOrUpdate: boolean;
  searchSwitch: boolean;
}

const headers = [
  { label: 'ID', key: 'id' },
  { label: '乗務員番号', key: 'user_no' },
  { label: '乗務員名', key: 'name' },
  { label: 'フリガナ', key: 'kana' },
  { label: '管理権限', key: 'role' },
  { label: '登録日時', key: 'created_at' },
  { label: '更新日時', key: 'updated_at' }
];

const GetCsvFileName = () => {
  const date = new Date();
  const hour =
    date.getMonth().toString().length === 1
      ? '0' + (date.getMonth() + 1).toString()
      : date.getMonth().toString();
  const day =
    date.getDate().toString().length === 1
      ? '0' + date.getDate().toString()
      : date.getDate().toString();
  return 'admin-users-' + date.getFullYear() + '-' + hour + '-' + day;
};

const pageDispMax = 30;
const pageMax = 6;
const csvFileName = GetCsvFileName();

export const MaintenanceAdminUsersTable: React.FC<props> = (props) => {
  const {
    AdminUsersDatas,
    setMaintenanceDatas,
    setChooseData,
    onOpenBrowse,
    onOpenEdit,
    onToggle,
    Query,
    setQuery,
    createOrUpdate,
    searchSwitch
  } = props;
  const [pageNow, setPageNow] = useState<number>(1);
  const [columnSortType, setColumnSortType] = useState<{
    key: string;
    sort: boolean;
  }>({ key: '', sort: false });

  const GetPageCount = (): number => {
    if (AdminUsersDatas !== undefined) {
      const divisionCount = Math.floor(AdminUsersDatas.length / pageDispMax);
      const remCount = AdminUsersDatas.length % pageDispMax === 0 ? 0 : 1;
      return divisionCount + remCount;
    }
    return 1;
  };

  const ColumnNameTriangleType = (column: string) => {
    if (columnSortType.key === column) {
      if (columnSortType.sort) {
        return TriangleUpIcon;
      } else {
        return TriangleDownIcon;
      }
    }
    return ArrowUpDownIcon;
  };

  const ColumnSortApi = async (column: string) => {
    const params = new URLSearchParams();
    const sortType = !columnSortType.sort;
    params.append('order', column + (sortType ? '_asc' : '_desc'));

    if (Query.crewNumber.number !== '') {
      switch (Query.crewNumber.compare) {
        case MaintenanceCompareEnum.Equal:
          params.append('user_no_eq', Query.crewNumber.number);
          break;
        case MaintenanceCompareEnum.Greater:
          params.append('user_no_gt', Query.crewNumber.number);
          break;
        case MaintenanceCompareEnum.Less:
          params.append('user_no_lt', Query.crewNumber.number);
          break;
      }
    }

    Query.crewName !== '' && params.append('name_contains', Query.crewName);
    Query.crewFurigana !== '' &&
      params.append('kana_contains', Query.crewFurigana);
    Query.role !== RoleEnum.Any.toString() &&
      params.append('role_eq', Query.role);
    Query.dateCreatedFrom !== '' &&
      params.append('created_at_gte', Query.dateCreatedFrom);
    Query.dateCreatedTo !== '' &&
      params.append('created_at_lt', Query.dateCreatedTo);
    Query.dateUpdatedFrom !== '' &&
      params.append('updated_at_gte', Query.dateUpdatedFrom);
    Query.dateUpdatedTo !== '' &&
      params.append('updated_at_lt', Query.dateUpdatedTo);
    const query = '/?' + params.toString();

    setQuery((pre) => ({ ...pre, sort: { key: column, sortType: sortType } }));
    const response = await QueryAdminUsersApi(query);
    if (response.success && response.data !== null) {
      setMaintenanceDatas(response.data);
      setColumnSortType({ key: column, sort: sortType });
    } else {
      alert(column + 'のソートに失敗しました。');
    }
  };
  useEffect(() => {
    setPageNow(1);
  }, [searchSwitch]);

  useEffect(() => {
    setPageNow(1);
    setColumnSortType({ key: '', sort: false });
  }, [createOrUpdate]);

  return (
    <>
      <TableContainer
        position='absolute'
        w='100%'
        h='100%'
        ml={5}
        overflowY='auto'
        gridRow='2/3'
        gridColumn='1/2'
        overflowX='unset'
      >
        <Table variant='simple'>
          <Thead borderWidth='1px' position='sticky' top={0} zIndex='docked'>
            <Tr bg='gray.800'>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.id)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>ID</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.id)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.user_no)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>乗務員番号</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.user_no)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.name)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>乗務員名</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.name)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.kana)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>フリガナ</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.kana)}
                  />
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>管理権限</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>登録日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>更新日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'></Th>
            </Tr>
          </Thead>
          <Tbody>
            {AdminUsersDatas !== undefined &&
              AdminUsersDatas.map((data, i) => {
                if (
                  pageDispMax * pageNow - pageDispMax <= i &&
                  i < pageDispMax * pageNow
                ) {
                  return (
                    <Tr key={`${'adminUser_' + i}`}>
                      <Td>{data.id}</Td>
                      <Td>{data.user_no}</Td>
                      <Td>{data.name}</Td>
                      <Td>{data.kana}</Td>
                      <Td>{data.role}</Td>
                      <Td>{data.created_at}</Td>
                      <Td>{data.updated_at}</Td>
                      <Td>
                        <MaintenanceModalDispLabels
                          data={{ type: 'adminUsers', adminUsers: data }}
                          setChooseData={setChooseData}
                          onOpenBrowse={onOpenBrowse}
                          onOpenEdit={onOpenEdit}
                          onToggle={onToggle}
                        />
                      </Td>
                    </Tr>
                  );
                }
              })}
          </Tbody>
        </Table>
      </TableContainer>
      <MaintenanceFooter
        CsvData={AdminUsersDatas !== undefined ? AdminUsersDatas : []}
        DataLength={AdminUsersDatas?.length}
        Headers={headers}
        CsvFileName={csvFileName}
        GetPageCount={GetPageCount}
        pageMax={pageMax}
        pageNow={pageNow}
        setPageNow={setPageNow}
      />
    </>
  );
};
